<template>
  <div class="server-artifacts" data-testid="server-document-artifacts">
    <article
      v-for="artifact in artifactRows"
      :key="artifact.alias"
      class="server-artifacts__row"
      :data-artifact="artifact.alias"
    >
      <div class="server-artifacts__copy">
        <strong>{{ artifact.label }}</strong>
        <p>{{ description(artifact) }}</p>
        <dl v-if="stateFor(artifact).metadata" class="server-artifacts__metadata">
          <div><dt>Файл</dt><dd>{{ stateFor(artifact).metadata.filename }}</dd></div>
          <div v-if="stateFor(artifact).metadata.formRevision"><dt>Редакция формы</dt><dd>{{ stateFor(artifact).metadata.formRevision }}</dd></div>
          <div v-if="stateFor(artifact).metadata.formVersion"><dt>Версия</dt><dd>{{ stateFor(artifact).metadata.formVersion }}</dd></div>
          <div><dt>Источник</dt><dd>{{ sourceLabel(stateFor(artifact).metadata) }}</dd></div>
        </dl>
        <p
          v-if="stateFor(artifact).message"
          class="server-artifacts__message"
          :class="`server-artifacts__message--${stateFor(artifact).status}`"
          role="status"
        >
          {{ stateFor(artifact).message }}
        </p>
        <ul v-if="stateFor(artifact).diagnostics.length" class="server-artifacts__diagnostics">
          <li v-for="(diagnostic, index) in stateFor(artifact).diagnostics" :key="`${artifact.alias}-${index}`">
            <span>
              {{ diagnostic.message || diagnostic.code }}
              <span v-if="diagnostic.fieldPath">· {{ diagnostic.fieldPath }}</span>
            </span>
            <span v-if="diagnostic.expected" class="server-artifacts__diagnostic-detail">{{ expectedLabel(diagnostic) }}: {{ diagnostic.expected }}</span>
            <span v-if="diagnostic.actual" class="server-artifacts__diagnostic-detail">Детали проверки: {{ diagnostic.actual }}</span>
            <q-btn
              v-if="hasDiagnosticTarget(diagnostic)"
              flat
              dense
              no-caps
              color="primary"
              label="Показать в документе"
              :aria-label="`Показать в документе: ${diagnostic.message || diagnostic.code}`"
              :data-testid="`artifact-diagnostic-${artifact.alias}-${index}`"
              @click="$emit('diagnostic', diagnostic)"
            />
          </li>
        </ul>
      </div>
      <div class="server-artifacts__actions">
        <q-btn
          v-if="artifact.kind === 'pdf'"
          outline
          no-caps
          color="primary"
          icon="open_in_new"
          label="Открыть"
          :loading="stateFor(artifact).pending === 'open'"
          :disable="disabled || Boolean(stateFor(artifact).pending)"
          :data-testid="`artifact-open-${artifact.alias}`"
          @click="openArtifact(artifact)"
        />
        <q-btn
          unelevated
          no-caps
          color="primary"
          icon="download"
          label="Скачать"
          :loading="stateFor(artifact).pending === 'download'"
          :disable="disabled || Boolean(stateFor(artifact).pending)"
          :data-testid="`artifact-download-${artifact.alias}`"
          @click="downloadArtifact(artifact)"
        />
        <q-btn
          v-if="canRetryCurrentForm(artifact)"
          flat
          no-caps
          color="primary"
          label="По текущей форме"
          :disable="disabled || Boolean(stateFor(artifact).pending)"
          @click="downloadArtifact(artifact, 'current')"
        />
      </div>
      <div v-if="stateFor(artifact).previewUrl" class="server-artifacts__preview">
        <div class="server-artifacts__preview-toolbar">
          <strong>Предпросмотр {{ stateFor(artifact).metadata.filename }}</strong>
          <q-btn flat round dense icon="close" aria-label="Закрыть предпросмотр" @click="closePreview(artifact.alias)" />
        </div>
        <object
          :data="stateFor(artifact).previewUrl"
          type="application/pdf"
          aria-label="Предпросмотр PDF с сервера"
        >
          <p>Встроенный просмотр недоступен. Используйте кнопку «Скачать».</p>
        </object>
      </div>
    </article>
  </div>
</template>

<script>
import {
  ArtifactRequestError,
  artifactErrorMessage,
  documentArtifactsFor,
  fetchDocumentArtifact,
  triggerBlobDownload
} from '../../services/backendArtifacts'

function emptyState() {
  return { status: 'idle', pending: '', message: '', diagnostics: [], metadata: null, previewUrl: '' }
}

export default {
  name: 'ServerDocumentPreview',
  emits: ['diagnostic'],
  props: {
    documentType: { type: String, required: true },
    documentId: { type: [String, Number], required: true },
    orgId: { type: [String, Number], required: true },
    historyId: { type: [String, Number], default: 'last' },
    offline: { type: Boolean, default: false },
    canRead: { type: Boolean, default: true }
  },
  data() {
    return { states: {}, controllers: {}, popupUrls: new Set() }
  },
  computed: {
    artifactRows() {
      return documentArtifactsFor(this.documentType)
    },
    disabled() {
      return this.offline || !this.canRead
    },
    isHistorical() {
      return this.historyId !== undefined && this.historyId !== null && this.historyId !== '' && this.historyId !== 'last'
    }
  },
  watch: {
    documentId: 'reset',
    historyId: 'reset',
    documentType: 'reset'
  },
  beforeUnmount() {
    this.abortAll()
    Object.keys(this.states).forEach(this.closePreview)
    this.revokePopupUrls()
  },
  methods: {
    stateFor(artifact) {
      return this.states[artifact.alias] || emptyState()
    },
    description(artifact) {
      if (artifact.kind === 'xml') return 'Электронный формат формируется backend-ом из сохраненной версии документа.'
      return 'Печатная форма формируется backend-ом; экранная карточка не используется как PDF-шаблон.'
    },
    sourceLabel(metadata) {
      const mode = metadata && metadata.selectionMode
      if (this.isHistorical) {
        return mode === 'current'
          ? `История №${this.historyId}, текущая редакция формы`
          : `История №${this.historyId}, редакция формы на дату документа`
      }
      return mode === 'current' ? 'Текущий документ, текущая редакция формы' : 'Текущий документ'
    },
    expectedLabel(diagnostic) {
      if (diagnostic.code === 'fns_xsd_validation_failed') return 'Схема'
      if (diagnostic.code === 'fns_control_validation_failed') return 'Контрольное условие'
      return 'Ожидалось'
    },
    setState(alias, patch) {
      this.states[alias] = { ...this.stateFor({ alias }), ...patch }
    },
    requestFor(artifact, formVersion, signal) {
      return fetchDocumentArtifact({
        connection: this.connection,
        documentId: this.documentId,
        orgId: this.orgId,
        historyId: this.historyId,
        artifactName: artifact.alias,
        formVersion,
        signal
      })
    },
    begin(artifact, intent) {
      this.controllers[artifact.alias]?.abort()
      const controller = new AbortController()
      this.controllers[artifact.alias] = controller
      this.setState(artifact.alias, { pending: intent, status: 'pending', message: 'Получаем файл с сервера…', diagnostics: [] })
      return controller
    },
    finish(artifact, result, message) {
      this.setState(artifact.alias, {
        pending: '',
        status: 'success',
        message,
        diagnostics: [],
        metadata: result.metadata
      })
      delete this.controllers[artifact.alias]
    },
    fail(artifact, error) {
      if (error && error.name === 'AbortError') return
      this.setState(artifact.alias, {
        pending: '',
        status: 'error',
        message: artifactErrorMessage(error),
        diagnostics: Array.isArray(error && error.diagnostics) ? error.diagnostics : [],
        lastErrorCode: error && error.code
      })
      delete this.controllers[artifact.alias]
    },
    hasDiagnosticTarget(diagnostic) {
      return Boolean(diagnostic && (diagnostic.fieldPath || Number.isInteger(diagnostic.lineIndex)))
    },
    async downloadArtifact(artifact, formVersion) {
      if (this.disabled || this.stateFor(artifact).pending) return
      const controller = this.begin(artifact, 'download')
      try {
        const result = await this.requestFor(artifact, formVersion, controller.signal)
        triggerBlobDownload(result.blob, result.metadata.filename)
        this.finish(artifact, result, `Файл ${result.metadata.filename} скачан.`)
      } catch (error) {
        this.fail(artifact, error)
      }
    },
    async openArtifact(artifact, formVersion) {
      if (this.disabled || this.stateFor(artifact).pending) return
      const mobile = this.$q.screen.lt.sm
      const mobilePopup = mobile ? window.open('', '_blank') : null
      if (mobile && !mobilePopup) {
        this.fail(artifact, new ArtifactRequestError({ code: 'popup_blocked' }))
        return
      }
      if (mobilePopup) mobilePopup.opener = null
      const controller = this.begin(artifact, 'open')
      try {
        const result = await this.requestFor(artifact, formVersion, controller.signal)
        const previewUrl = URL.createObjectURL(result.blob)
        this.closePreview(artifact.alias)
        if (mobilePopup) {
          this.popupUrls.add(previewUrl)
          const release = () => this.revokePopupUrl(previewUrl)
          mobilePopup.addEventListener('load', release, { once: true })
          mobilePopup.location.replace(previewUrl)
          setTimeout(release, 60 * 1000)
        } else {
          this.setState(artifact.alias, { previewUrl })
        }
        this.finish(artifact, result, `Файл ${result.metadata.filename} готов к просмотру.`)
      } catch (error) {
        mobilePopup?.close()
        this.fail(artifact, error)
      }
    },
    canRetryCurrentForm(artifact) {
      return this.isHistorical && this.stateFor(artifact).lastErrorCode === 'form_version_not_found'
    },
    closePreview(alias) {
      const state = this.states[alias]
      if (!state || !state.previewUrl) return
      URL.revokeObjectURL(state.previewUrl)
      this.states[alias] = { ...state, previewUrl: '' }
    },
    revokePopupUrl(url) {
      if (!this.popupUrls.has(url)) return
      URL.revokeObjectURL(url)
      this.popupUrls.delete(url)
    },
    revokePopupUrls() {
      Array.from(this.popupUrls).forEach(this.revokePopupUrl)
    },
    abortAll() {
      Object.values(this.controllers).forEach((controller) => controller.abort())
      this.controllers = {}
    },
    reset() {
      this.abortAll()
      Object.keys(this.states).forEach(this.closePreview)
      this.revokePopupUrls()
      this.states = {}
    }
  }
}
</script>

<style scoped>
.server-artifacts { display: grid; gap: var(--sid-space-3); }
.server-artifacts__row { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: var(--sid-space-4); padding: var(--sid-space-4) 0; border-bottom: 1px solid var(--sid-color-line); }
.server-artifacts__row:last-child { border-bottom: 0; }
.server-artifacts__copy { min-width: 0; }
.server-artifacts__copy > p { margin: var(--sid-space-1) 0 0; color: var(--sid-color-text-secondary); }
.server-artifacts__actions { display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: flex-end; gap: var(--sid-space-2); }
.server-artifacts__metadata { display: flex; flex-wrap: wrap; gap: var(--sid-space-2) var(--sid-space-5); margin: var(--sid-space-3) 0 0; }
.server-artifacts__metadata div { min-width: 0; }
.server-artifacts__metadata dt { color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-caption); }
.server-artifacts__metadata dd { margin: 0; overflow-wrap: anywhere; }
.server-artifacts__message { font-size: var(--sid-font-size-label); }
.server-artifacts__message--error { color: var(--sid-color-danger); }
.server-artifacts__message--success { color: var(--sid-color-success); }
.server-artifacts__diagnostics { grid-column: 1 / -1; display: grid; gap: var(--sid-space-2); margin: var(--sid-space-2) 0 0; padding-left: var(--sid-space-5); color: var(--sid-color-danger); }
.server-artifacts__diagnostics li { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: var(--sid-space-2); }
.server-artifacts__diagnostic-detail { flex-basis: 100%; overflow-wrap: anywhere; }
.server-artifacts__preview { grid-column: 1 / -1; min-width: 0; border: 1px solid var(--sid-color-line); border-radius: var(--sid-radius-medium); overflow: hidden; background: var(--sid-color-surface-1); }
.server-artifacts__preview-toolbar { display: flex; align-items: center; justify-content: space-between; gap: var(--sid-space-3); padding: var(--sid-space-2) var(--sid-space-3); border-bottom: 1px solid var(--sid-color-line); }
.server-artifacts__preview object { display: block; width: 100%; height: min(70vh, 52rem); border: 0; }
@media (max-width: 599px) {
  .server-artifacts__row { grid-template-columns: 1fr; }
  .server-artifacts__actions { justify-content: stretch; }
  .server-artifacts__actions :deep(.q-btn) { flex: 1 1 auto; min-height: 44px; }
  .server-artifacts__preview { display: none; }
}
</style>
